// KraepQuestionPage.jsx
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { BaseService } from '../../services/BaseService'
import { useQuestionDispatch } from '../../context/QuestionContext/QuestionContext'
import { useModule } from '../../context/ModuleContext/ModuleContext'
import { useGetUser } from '../../hooks/useGetUser'
import { ADD_QUESTIONS_LIST } from '../../reducers/reducesTypes'
import { moduleTypeIncludes, modulesType } from '../../constants/modules'
import WizardKraep from '../../components/questions/WizardKraep'
import NumberChoiceQuestion from '../../components/questions/NumberChoiceQuestion'
import { useCameraContext } from '../../context/CameraContext/CameraContext'
import { useGeolocationContext } from '../../context/GeolocationContext/GeolocationContext'
import { calculateSessionTime } from '../../utils/calculateSessionTime'
import { getCurrentPosition } from '../../utils/geolocation'
import { useLogging } from '../../context/LoggingContext/LoggingContext'
import useAntiFocus from '../../hooks/useAntiFocus'
import useAntiScreenshot from '../../hooks/useAntiScreenshot'
import { getAudioContext, playWarningBeep } from '../../context/AudioContext/AudioContext'
import { buildCameraTargets } from '../../utils/buildCameraTargets'
import { getPendingCameraTargets } from '../../utils/shouldCaptureCamera'
import { useQeustion } from '../../context/QuestionContext/QuestionContext'
import { removeLocalStorageData } from '../../utils/storage/storage'
import ProctorOverlay from '../../components/Loader/ProctorOverlay'

// === API Calls ===
async function fetchHistory(userId) {
  const service = new BaseService('/m_history_2')
  const res = await service.getHistory({ id_user: userId })
  return res.data
}
async function fetchSoalPertype(subType = 'SE', id) {
  const service = new BaseService('/m_soal_pertype')
  const res = await service.getQuestion({ id_modul: id, sub_type: subType })
  return res.data
}
async function fetchSoalSubtype(id) {
  const service = new BaseService('/m_sub_type')
  const res = await service.getQuestion({ id_modul: id })
  return res.data
}

const KraepQuestionPage = () => {
  const { id } = useParams()
  const { user } = useGetUser()
  const dispatch = useQuestionDispatch()
  const { modules } = useModule()
  const {
    permissionGranted: cameraGranted,
    // startCapture,
    captureOnce,
    stopCapture,
    setModuleId,
    startCameraSession,
    requestCameraPermission,
  } = useCameraContext()
  const { permissionGranted: locationGranted } = useGeolocationContext()
  const [isCameraBlocked, setIsCameraBlocked] = useState(false)
  const [isLocationBlocked, setIsLocationBlocked] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { addLog } = useLogging()
  const { ScreenshotDialog } = useAntiScreenshot({
    userId: user.id,
    moduleId: id,
    addLog,
  })
  const isBeforeUnloadRef = useRef(false)
  const cameraStartedRef = useRef(false)

  const [subtypes, setSubtypes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentSubtypeIndex, setCurrentSubtypeIndex] = useState(0)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)

  const [openDialog, setOpenDialog] = useState(false)
  const [alreadyDoneDialog, setAlreadyDoneDialog] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState('')
  const [freeze, setFreeze] = useState(false)
  const [isEnded, setIsEnded] = useState(false)

  const [isFinalLoading, setIsFinalLoading] = useState(false)
  const [isFinalError, setIsFinalError] = useState(false)

  const { currentGlobal } = useQeustion()
  const isSubmittingRef = useRef(false)

  useAntiFocus({
    onBlur: () => {
      if (isEnded) return
      if (isBeforeUnloadRef.current) return

      playWarningBeep()
      addLog(user.id, id, 'Berpindah/Menyembunyikan Tab')
    },
    onHidden: () => {
      if (isEnded) return
      if (isBeforeUnloadRef.current) return

      playWarningBeep()
      addLog(user.id, id, 'Berpindah/Menyembunyikan Tab')
    },
  })

  // START: audio warning
  useEffect(() => {
    const shouldUnlock = localStorage.getItem('audioUnlockedIntent') === 'true'
    if (!shouldUnlock) return

    const ctx = getAudioContext()

    // 🔑 unlock via user gesture minimal
    const unlock = () => {
      if (ctx.state === 'suspended') {
        ctx.resume()
      }
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }

    document.addEventListener('click', unlock)
    document.addEventListener('keydown', unlock)

    return () => {
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }
  }, [])
  // END: audio warning

  // START: set flag before unload
  useEffect(() => {
    if (isEnded) {
      window.onbeforeunload = null
      return
    }

    const warnBeforeUnload = (e) => {
      isBeforeUnloadRef.current = true
      e.preventDefault()
      e.returnValue = 'Leave Page?'
    }

    window.addEventListener('beforeunload', warnBeforeUnload)
    window.onbeforeunload = warnBeforeUnload

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload)
      window.onbeforeunload = null
    }
  }, [isEnded])

  useEffect(() => {
    const reset = () => {
      // browser gak jadi unload → user klik "Cancel"
      isBeforeUnloadRef.current = false
    }

    window.addEventListener('focus', reset)
    return () => window.removeEventListener('focus', reset)
  }, [])
  // END: set flag before unload

  const rules = useMemo(() => {
    try {
      return typeof modules?.rules === 'string' ? JSON.parse(modules.rules) : modules?.rules || {}
    } catch {
      return {}
    }
  }, [modules?.rules])

  // helper: decide start subtype index based on localStorage
  function getStartIndexFromStorage(subtypesList) {
    try {
      const raw = localStorage.getItem('questionKraep')
      if (!raw) return 0
      const parsed = JSON.parse(raw)
      // if any subType still inProgress, return its index
      const inProgress = parsed.data?.find((d) => d.data?.status === 'inProgress')
      if (inProgress) {
        return subtypesList.findIndex((s) => s.type_code === inProgress.subType)
      }
      // otherwise pick first subtype not present in storage (first undone)
      const doneTypes = (parsed.data || []).map((d) => d.subType)
      const firstUndone = subtypesList.findIndex((s) => !doneTypes.includes(s.type_code))
      return firstUndone === -1 ? 0 : firstUndone
    } catch (e) {
      return 0
    }
  }

  useEffect(() => {
    if (rules?.camera !== 1) return
    if (!id) return

    requestCameraPermission() // 🚨 INI YANG BIKIN cameraGranted JADI TRUE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules?.camera, id])

  // START: access camera and location
  // blurred when camera and location is not granted
  // useEffect(() => {
  //   if (cameraGranted === false || locationGranted === false) {
  //     setIsBlocked(true)
  //   } else if (cameraGranted === true && locationGranted === true) {
  //     setIsBlocked(false)
  //   }
  // }, [cameraGranted, locationGranted])
  useEffect(() => {
    const needCamera = rules?.camera !== 0
    const needGeo = rules?.geo !== 0

    if ((needCamera && cameraGranted === false)) {
      setIsCameraBlocked(true)
    } else {
      setIsCameraBlocked(false)
    }

    if (needGeo && locationGranted === false) {
      setIsLocationBlocked(true)
    } else {
      setIsLocationBlocked(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraGranted, locationGranted, modules?.rules])

  // START: init session camera, new mechanism
  useEffect(() => {
    // if (rules?.camera === false) return
    if (rules?.camera !== 1) return
    if (!cameraGranted) return
    if (!id) return
    if (cameraStartedRef.current) return

    cameraStartedRef.current = true
    startCameraSession()

    return () => {
      stopCapture()
      cameraStartedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraGranted, id, modules?.rules])
  // END: init session camera, new mechanism

  useEffect(() => {
    if (id) {
      setModuleId(Number(id)) // kasih tau camera modul mana
    }
  }, [id, setModuleId])
  // END: access camera and location

  useEffect(() => {
    fetchSoalSubtype(id).then((res) => {
      let subtypesData = []

      res.data.forEach((s) => {
        subtypesData.push(s)
      })

      setSubtypes(subtypesData)
      setLoading(false)

      const start = getStartIndexFromStorage(subtypesData)
      setCurrentSubtypeIndex(start)
    })
  }, [id])

  useEffect(() => {
    if (subtypes?.length > 0) {
      const subtype = subtypes[currentSubtypeIndex]
      setIsLoading(true)
      fetchSoalPertype(subtype.type_code, id)
        .then((res) => {
          setQuestions(res.data)
          dispatch({ type: ADD_QUESTIONS_LIST, list: res.data })
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtypes, currentSubtypeIndex, id])

  // function handleFinishSubtype(answers) {
  //   // pindah ke subtype berikutnya
  //   if (currentSubtypeIndex < subtypes.length - 1) {
  //     setCurrentSubtypeIndex((prev) => prev + 1)
  //   } else {
  //     navigate('/') // selesai semua
  //   }
  // }

  // function handleFinishSubtype() {
  //   if (currentSubtypeIndex < subtypes?.length - 1) {
  //     setCurrentSubtypeIndex((prev) => prev + 1) // lanjut ke subtype berikutnya
  //   } else {
  //     handleAllFinish() // baru submit semua subtest
  //     setIsOpen(true)
  //     // navigate('/')
  //   }
  // }
  async function handleFinishSubtype() {
    if (isEnded) return
    if (isSubmittingRef.current) return

    if (currentSubtypeIndex < subtypes.length - 1) {
      setCurrentSubtypeIndex((prev) => prev + 1)
    } else {
      await handleAllFinish()
      setIsOpen(true)
    }
  }

  // taro di KraepQuestionPage
  async function submitAllSubtests(user, id_modul) {
    const raw = localStorage.getItem('questionKraep')
    if (!raw) return

    const parsed = JSON.parse(raw)
    const subTypesToSkip = ['ME_IMAGE', '_DESC_IST']

    const answers = await Promise.all(
      parsed.data
        .filter(
          (entry) =>
            !subTypesToSkip.some((s) => entry.subType?.toLowerCase().includes(s.toLowerCase())),
        )
        .map(async (entry) => {
          return {
            type_soal: entry?.subType,
            answer: transformKeys(entry?.data.questions),
          }
        }),
    )

    const { time_start, time_end, durasi } = calculateSessionTime(
      JSON.parse(localStorage.getItem('questionKraep')),
    )

    let location = null

    // if (rules?.geo !== 0) {
    //   try {
    //     location = await getCurrentPosition({ timeout: 7000 })
    //   } catch (err) {
    //     console.warn('Gagal ambil lokasi:', err.message)
    //   }
    // }

    if (rules?.geo !== 0) {
      try {
        location = await getCurrentPosition({ timeout: 15000 })
      } catch (err) {
        const cached = localStorage.getItem('geoLoc')
        if (cached) {
          location = JSON.parse(cached)
        } else {
          console.warn('Gagal ambil lokasi:', err.message)
        }
      }
    }

    const payload = {
      id_user: user.id,
      id_modul,
      tipe_modul: modules?.tipe_modul,
      json_jawaban: JSON.stringify(answers),
      time_Start: time_start,
      time_End: time_end,
      durasi,
      latitude: location?.lat || null,
      longitude: location?.lon || null,
    }

    const service = new BaseService('/m_activity_2')
    const res = await service.postAnswer(payload)

    return res
  }

  // const handleAllFinish = async () => {
  //   try {
  //     const res = await submitAllSubtests(user, id, subtype)
  //     setModalContent(res?.data?.msg)
  //     localStorage.removeItem('questionKraep')

  //     // ❄️ Freeze timer biar gak jalan lagi
  //     setFreeze(true)

  //     // Tandai ujian selesai
  //     setIsEnded(true)

  //     // Stop kamera & tracking
  //     stopCapture()

  //     addLog(user.id, id, 'Selesai mengerjakan test')
  //     // console.log('All subtests submitted!')
  //   } catch (e) {
  //     console.error('Submit all failed', e)
  //   }
  // }
  const handleAllFinish = async () => {
    if (isSubmittingRef.current) return
    setIsFinalLoading(true)
    setIsFinalError(false)
    isSubmittingRef.current = true

    const raw = localStorage.getItem('questionKraep')

    if (raw) {
      const parsed = JSON.parse(raw)
      const camera = parsed.camera
      const globalCount = parsed.globalCount

      const pending = getPendingCameraTargets(camera, globalCount)

      if (rules?.camera === 1 && pending.length > 0) {
        console.log('🔥 FORCE LAST KRAEP CAPTURE (Pending) @', globalCount, 'Targets:', pending)
        await captureOnce?.(globalCount)

        const updated = {
          ...parsed,
          camera: {
            ...camera,
            captured: [...camera.captured, ...pending],
          },
        }

        localStorage.setItem('questionKraep', JSON.stringify(updated))
      }
    }

    try {
      // freeze SEBELUM async
      setFreeze(true)
      setIsEnded(true)

      const res = await submitAllSubtests(user, id)
      setIsFinalLoading(false)
      setModalContent(res?.data?.msg)
      localStorage.removeItem('questionKraep')
      stopCapture()

      // 🔒 log juga kena lock ini
      await addLog(user.id, id, 'Selesai mengerjakan test')
    } catch (e) {
      console.error('Submit all failed', e)
      setIsFinalError(true)
      setIsFinalLoading(false)
      isSubmittingRef.current = false // rollback kalau gagal
      setModalContent('Koneksi terputus. Gagal mengirimkan ribuan data Kraepelin Anda.')
    }
  }

  // === Queries ===
  const { data: dataHistory } = useQuery(['rules-history', id], () => fetchHistory(user.id))

  const checkDoneTask = dataHistory?.data?.some((h) => h.id === id)

  // === Navigation Guard (Back Button) ===
  const handlePopstate = useCallback(
    (event) => {
      if (event.state && modules?.startQuestion) {
        navigate(location.pathname, { replace: true })
      }
    },
    [modules?.startQuestion, navigate, location.pathname],
  )

  useEffect(() => {
    window.addEventListener('popstate', handlePopstate)
    return () => window.removeEventListener('popstate', handlePopstate)
  }, [handlePopstate])



  // START: camera new mechanism
  useEffect(() => {
    // if (rules?.camera === false) return
    if (rules?.camera !== 1) return
    if (!subtypes.length) return

    const raw = localStorage.getItem('questionKraep')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed.camera) return // jangan override
    }

    const TOTAL_PER_SUBTYPE = 27
    const totalQuestion = subtypes.length * TOTAL_PER_SUBTYPE

    const initial = {
      userId: user.id,
      moduleID: Number(id),
      time_start: new Date().toISOString(),
      camera: {
        layout: 9,
        targets: buildCameraTargets(totalQuestion, 9),
        captured: [],
      },
      data: [],
      globalCount: 0,
    }

    localStorage.setItem('questionKraep', JSON.stringify(initial))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtypes, id, user.id, modules?.rules])

  useEffect(() => {
    // if (rules?.camera === false) return
    if (rules?.camera !== 1) return
    const raw = localStorage.getItem('questionKraep')
    if (!raw) return

    const parsed = JSON.parse(raw)
    const camera = parsed.camera
    if (!camera) return

    const globalCount = parsed.globalCount

    const pending = getPendingCameraTargets(camera, globalCount)

    if (rules?.camera === 1 && pending.length > 0) {
      console.log('KRAEP CAPTURE (Missed or Hit) @', globalCount, 'Targets:', pending)

      captureOnce?.(globalCount)

      const updated = {
        ...parsed,
        camera: {
          ...camera,
          captured: [...camera.captured, ...pending], // Tandai semua target tertunda sebagai selesai
        },
      }

      localStorage.setItem('questionKraep', JSON.stringify(updated))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGlobal, modules?.rules]) // gua ngambil parsed dari mana ajigh?
  // END: camera new mechanism

  useEffect(() => {
    if (rules?.geo !== 0) {
      getCurrentPosition({ timeout: 15000 })
        .then((loc) => {
          localStorage.setItem('geoLoc', JSON.stringify(loc))
        })
        .catch(() => {})
    }
  }, [rules?.geo])

  function renderQuestion(moduleType, question, idx) {
    switch (moduleType) {
      case modulesType.NUMBER_CHOICE:
        return <NumberChoiceQuestion key={idx} dataQuestion={question} indices={idx + 1} />
      default:
        return (
          <Box key={idx}>
            <Typography>
              Oops! <br /> Maaf soal belum ada, stay tuned 😀
            </Typography>
          </Box>
        )
    }
  }

  function transformKeys(values) {
    const newValues = {}
    Object.entries(values).forEach(([key, val]) => {
      // hapus prefix "question_"
      const newKey = key.replace(/^question_/, '')
      newValues[newKey] = val
    })
    return newValues
  }

  function handleData(moduleType, questionsList) {
    if (!questionsList) return []
    let filtered = questionsList
    if (moduleType === modulesType.MULTIPLE) {
      filtered = questionsList.filter(
        (q) => q.pilihan_1 || q.pilihan_2 || q.pilihan_3 || q.pilihan_4,
      )
    }
    return filtered.map((q, idx) => renderQuestion(moduleType, q, idx))
  }

  if (!moduleTypeIncludes.includes(modules.tipe_modul)) {
    return (
      <Box
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Typography fontWeight={700}>Oops!</Typography>
        <Typography mt={1}>Maaf soal ini belum tersedia. Hubungi Dika Psikotes 😀</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 3, fontWeight: 700, color: 'white' }}
        >
          Kembali
        </Button>
      </Box>
    )
  }

  if (checkDoneTask) {
    return (
      <Dialog open={alreadyDoneDialog}>
        <DialogContent>Module sudah pernah dikerjakan.</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setAlreadyDoneDialog(false)
              removeLocalStorageData('module')
              removeLocalStorageData('questionKraep')
              removeLocalStorageData('camera_capture_log')
              // navigate('/', { replace: true })
              window.location.href = (process.env.REACT_APP_BASE_NAME || '') + '/'
            }}
            sx={{ backgroundColor: ({ palette }) => palette.warning.light, color: 'white' }}
          >
            Kembali
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (loading) return <Skeleton variant="rectangular" width="100%" height={200} />

  const subtype = subtypes[currentSubtypeIndex]

  return (
    <Box height="100%" width="100%">
      {!isLoading ? (
        <WizardKraep
          key={subtype.type_code}
          questions={questions}
          id_modul={id}
          subtype={subtype}
          onFinish={handleFinishSubtype}
          freeze={freeze}
        >
          {handleData(subtype.type_sub_modul, questions)}
        </WizardKraep>
      ) : (
        [...Array(5)].map((_, idx) => (
          <Skeleton
            key={idx}
            height={idx === 0 ? '250px' : '80px'}
            width="100%"
            sx={{ transform: 'scale(1,.8)' }}
          />
        ))
      )}

      <Dialog open={openDialog}>
        <DialogContent>Sesi masih berlangsung, harap selesaikan terlebih dahulu</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(false)}
            sx={{ backgroundColor: ({ palette }) => palette.warning.light, color: 'white' }}
          >
            Lanjutkan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOpen}
        // Kunci modal, gak boleh ditutup sembarangan lewat backdrop/escape
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return
          setIsOpen(false)
        }}
      >
        <DialogContent sx={{ textAlign: 'center', minWidth: '320px' }}>
          {isFinalLoading && (
            <Box sx={{ py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography fontWeight="bold">Mengunggah Hasil Kraepelin...</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Mohon tunggu, kami sedang memproses data jawaban Anda yang cukup besar. Jangan tutup
                halaman ini.
              </Typography>
            </Box>
          )}

          {isFinalError && (
            <Box sx={{ py: 2 }}>
              <Typography color="error" variant="h6" fontWeight="bold">
                Gagal Mengirim Jawaban
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 3 }}>
                {modalContent}
              </Typography>
              <Button variant="contained" fullWidth color="primary" onClick={handleAllFinish}>
                Kirim Ulang Jawaban
              </Button>
            </Box>
          )}

          {!isFinalLoading && !isFinalError && (
            <Box sx={{ py: 1 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                Berhasil!
              </Typography>
              <Typography>{modalContent}</Typography>
              <Typography sx={{ mt: 2, variant: 'body2', color: 'textSecondary' }}>
                Silahkan klik beranda untuk kembali ke halaman utama
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {!isFinalLoading && !isFinalError && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setIsOpen(false)
                removeLocalStorageData('module')
                removeLocalStorageData('questionKraep')
                removeLocalStorageData('camera_capture_log')
                removeLocalStorageData('geoLoc')
                window.location.href = (process.env.REACT_APP_BASE_NAME || '') + '/'
              }}
              sx={{ backgroundColor: ({ palette }) => palette.success.light, color: 'white' }}
            >
              Beranda
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ProctorOverlay
        open={isCameraBlocked}
        title="⚠️ Kamera nonaktif"
      />

      <ProctorOverlay
        open={isLocationBlocked}
        title="⚠️ Lokasi nonaktif"
      />

      <ScreenshotDialog />
    </Box>
  )
}

export default KraepQuestionPage
